import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import _ from 'lodash';

import { clearTokenServer } from './auth.server';
import { clearTokenClient } from './auth.client';
import { IMAGE_EXTENSIONS } from '../consts/file.const';
import { useAuthStore } from '../stores/auth.store';
import { refreshTokenService } from './refresh-token';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const logout = async (redirectToLogin = true) => {
  try {
    const userId = useAuthStore.getState().user.id;

    if (userId) {
      useAuthStore.getState().setOfflineStatus(userId);
    }

    useAuthStore.getState().clearUser();

    // Revoke refresh token on server
    await refreshTokenService.revokeRefreshToken();

    clearTokenClient();
    await clearTokenServer();
    
    // Clear refresh token from localStorage
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    
    // Redirect to login page if requested
    if (redirectToLogin && typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  } catch (error) {
    // Still clear local tokens even if API call fails
    useAuthStore.getState().clearUser();
    clearTokenClient();
    await clearTokenServer();
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    
    // Redirect to login page even if logout failed
    if (redirectToLogin && typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }
};

// Logout for admin - redirect to logout page
export const logoutAdmin = async () => {
  try {
    const userId = useAuthStore.getState().user.id;

    if (userId) {
      useAuthStore.getState().setOfflineStatus(userId);
    }

    useAuthStore.getState().clearUser();

    // Revoke refresh token on server
    await refreshTokenService.revokeRefreshToken();

    clearTokenClient();
    await clearTokenServer();
    
    // Clear refresh token from localStorage
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    
    // Redirect to logout page for admin
    if (typeof window !== 'undefined') {
      window.location.href = '/logout';
    }
  } catch (error) {
    // Still clear local tokens even if API call fails
    useAuthStore.getState().clearUser();
    clearTokenClient();
    await clearTokenServer();
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    
    // Redirect to logout page even if logout failed
    if (typeof window !== 'undefined') {
      window.location.href = '/logout';
    }
  }
};

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: 'accurate' | 'normal';
  } = {},
) {
  const { decimals = 0, sizeType = 'normal' } = opts;

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];

  if (bytes === 0) return '0 Byte';

  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === 'accurate' ? (accurateSizes[i] ?? 'Bytes') : (sizes[i] ?? 'Bytes')
  }`;
}

export const formatMoney = (money: number, showPrefix = false, type = 'en-US') => {
  if (money === undefined || money === null) return '';

  const formatter = new Intl.NumberFormat(type, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  if (showPrefix) {
    return formatter.format(money);
  }

  return new Intl.NumberFormat(type).format(money);
};
export function isValidImage(name: string, bytes: number): boolean {
  return IMAGE_EXTENSIONS.includes(name.split('.').pop() ?? '') && bytes < 20 * Math.pow(1024, 2);
}

export const generateColorFromName = (name: string): string => {
  let hash = 0;

  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FECA57',
    '#FF9FF3',
    '#54A0FF',
    '#5F27CD',
    '#00D2D3',
    '#FF9F43',
    '#10AC84',
    '#EE5A24',
    '#0984E3',
    '#6C5CE7',
    '#A29BFE',
    '#FD79A8',
    '#E84393',
    '#00B894',
    '#00CEC9',
    '#E17055',
  ];

  const index = Math.abs(hash) % colors.length;

  return colors[index];
};

export const getInitials = (name: string): string => {
  if (!name) return '?';

  const words = name.trim().split(' ');

  return words[0].charAt(0).toUpperCase();
};

export function areAllPropertiesEmpty(obj: Record<string, any>) {
  if (!_.isObject(obj) || _.isNull(obj)) {
    return true;
  }

  const values = _.values(obj);

  if (values.length === 0 && _.isEmpty(obj)) {
    return true;
  }

  return _.every(values, _.isEmpty);
}

export const unsecuredCopyToClipboard = (text: string) => {
  const textArea = document.createElement('textarea');

  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand('copy');
  } catch (err) {
    // Unable to copy to clipboard
  }
  document.body.removeChild(textArea);
};
